// Full poll-by-poll inspection table.
//
// Class names (`pl-bg`, `pn-bg`, `pl-num`, `pn-num`, `winning`, `lead-cell`,
// `lead-pl`, `lead-pn`, `actual-result`, `source-link`, `pollster`, `date`,
// `notes`, `num`) are kept identical to the inspection HTML so the cell
// treatments map 1:1 to the spec without re-thinking the visual rules.

import { useMemo, useState } from 'react';
import { POLLSTERS } from '../../data/surveys';
import type { Poll, PollsterName } from '../../data/surveys.types';
import { usePolls } from '../../lib/usePolls';
import './Surveys.css';

// Short display names for the pollster column. The methodology section
// uses the long names; here we want the table-cell-friendly versions
// from the inspection HTML.
const POLLSTER_DISPLAY: Record<PollsterName, string> = {
  MaltaToday: 'MaltaToday',
  Sagalytics: 'Sagalytics',
  'Esprimi (Times of Malta)': 'Esprimi',
  '2024 EP Election (actual result)': '2024 EP Election',
};

const isAnchor = (p: Poll): boolean =>
  p.pollster === '2024 EP Election (actual result)';

type SortOrder = 'newest' | 'oldest';

export default function InspectionTable() {
  const { polls } = usePolls();
  const [order, setOrder] = useState<SortOrder>('newest');

  const sorted = useMemo(() => {
    const cmp = (a: Poll, b: Poll) => a.date.localeCompare(b.date);
    return order === 'newest' ? [...polls].sort((a, b) => cmp(b, a)) : [...polls].sort(cmp);
  }, [polls, order]);

  return (
    <>
      <div className="inspection-table__toolbar">
        <span className="inspection-table__toolbar-label">Sort</span>
        <div
          className="inspection-table__sort"
          role="group"
          aria-label="Sort by poll date"
        >
          <button
            type="button"
            className={`inspection-table__sort-btn${order === 'newest' ? ' is-active' : ''}`}
            aria-pressed={order === 'newest'}
            onClick={() => setOrder('newest')}
          >
            Newest first
          </button>
          <button
            type="button"
            className={`inspection-table__sort-btn${order === 'oldest' ? ' is-active' : ''}`}
            aria-pressed={order === 'oldest'}
            onClick={() => setOrder('oldest')}
          >
            Oldest first
          </button>
        </div>
      </div>

      <div className="inspection-table-wrap">
        <table className="inspection-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Pollster</th>
              <th className="num">N</th>
              <th className="num">PL %</th>
              <th className="num">PN %</th>
              <th className="num">ADPD</th>
              <th className="num">MOM</th>
              <th className="num">Other</th>
              <th className="num">Lead</th>
              <th className="num">Abela trust</th>
              <th className="num">Borg/Grech trust</th>
              <th className="num">Neither</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => (
              <Row key={`${p.date}|${p.pollster}`} poll={p} anchor={isAnchor(p)} />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Row ────────────────────────────────────────────────────────────
function Row({ poll, anchor }: { poll: Poll; anchor: boolean }) {
  const pollster = POLLSTERS[poll.pollster];
  const display = POLLSTER_DISPLAY[poll.pollster];

  // Whichever of PL/PN has the higher % gets the faint coloured cell
  // background and an extra-bold "winning" class. Equal values (rare /
  // hypothetical) fall through with no winner highlighted.
  const plLeads =
    poll.pl != null && poll.pn != null && poll.pl > poll.pn;
  const pnLeads =
    poll.pl != null && poll.pn != null && poll.pn > poll.pl;

  const leadColor =
    poll.lead > 0 ? 'lead-pl' : poll.lead < 0 ? 'lead-pn' : '';
  const leadSign = poll.lead > 0 ? '+' : '';
  const leadText = `${leadSign}${poll.lead.toFixed(1)}pp`;

  return (
    <tr className={anchor ? 'actual-result' : ''}>
      <td className="date">
        {poll.date}
        {poll.field && <span className="td-date-field">{poll.field}</span>}
      </td>

      <td className="pollster">
        <span className="pill" style={{ background: pollster.abbr_bg }}>
          {pollster.abbr}
        </span>
        {display}
      </td>

      <td className="num">
        <NumCell value={poll.sample} format={(v) => v.toLocaleString('en-MT')} />
      </td>

      <td className={['num', 'pl-num', plLeads ? 'pl-bg winning' : ''].join(' ').trim()}>
        <NumCell value={poll.pl} />
      </td>

      <td className={['num', 'pn-num', pnLeads ? 'pn-bg winning' : ''].join(' ').trim()}>
        <NumCell value={poll.pn} />
      </td>

      <td className="num">
        <NumCell value={poll.adpd} />
      </td>
      <td className="num">
        <NumCell value={poll.mom} />
      </td>
      <td className="num">
        <NumCell value={poll.other} />
      </td>

      <td className={['num', 'lead-cell', leadColor].filter(Boolean).join(' ')}>
        {leadText}
      </td>

      <td className="num">
        <NumCell value={poll.abela} />
      </td>

      <td className="num">
        <NumCell value={poll.opp} />
      </td>

      <td className="num">
        <NumCell value={poll.none} />
      </td>

      <td className="notes">
        {poll.url ? (
          <a
            href={poll.url}
            className="source-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            → source
          </a>
        ) : (
          <Dash />
        )}
      </td>
    </tr>
  );
}

function NumCell({
  value,
  suffix = '',
  format,
}: {
  value: number | null | undefined;
  suffix?: string;
  format?: (v: number) => string;
}) {
  if (value == null) return <Dash />;
  return (
    <>
      {format ? format(value) : value}
      {suffix}
    </>
  );
}

function Dash() {
  return <span className="dash">—</span>;
}
